// @steered AudibleSecurityContext 1.2 2026-09-21
/**
 * LambdaService - Direct Lambda invocation service using AWS SDK with Cognito authentication.
 * 
 * This service encapsulates all Lambda invocation logic for:
 * - StrandsAgents (us-west-2): Orchestrator Lambda for AI chat
 * - SlackWebhookFunction (us-west-2): Handles Slack submissions
 * - G2S2HelperFunction (us-east-1): G2S2 record operations
 * 
 * Requirements: 1.1, 1.2, 1.3, 1.4, 5.1, 5.3, 6.1, 6.2, 6.3, 6.4, 6.5
 */

import { LambdaClient, InvokeCommand, InvokeCommandOutput } from '@aws-sdk/client-lambda';
import { fetchAuthSession } from 'aws-amplify/auth';
import { lambdaConfig } from '../config/lambdaConfig';

// ============================================================================
// Error Types and Classes
// Requirements: 6.1, 6.2, 6.3, 6.5
// ============================================================================

/**
 * Enum representing the different types of Lambda invocation errors.
 */
export enum LambdaErrorType {
  TIMEOUT = 'TIMEOUT',
  AUTH_FAILURE = 'AUTH_FAILURE',
  NETWORK_ERROR = 'NETWORK_ERROR',
  LAMBDA_ERROR = 'LAMBDA_ERROR',
  PARSE_ERROR = 'PARSE_ERROR',
  UNKNOWN = 'UNKNOWN',
}

/**
 * Base error class for all Lambda-related errors.
 * Provides structured error information for UI handling.
 */
export class LambdaError extends Error {
  public readonly type: LambdaErrorType;
  public readonly functionName: string;
  public readonly region: string;
  public readonly isRetryable: boolean;
  public readonly originalError?: Error;
  public readonly timestamp: Date;

  constructor(
    message: string,
    type: LambdaErrorType,
    functionName: string,
    region: string,
    isRetryable: boolean = false,
    originalError?: Error
  ) {
    super(message);
    this.name = 'LambdaError';
    this.type = type;
    this.functionName = functionName;
    this.region = region;
    this.isRetryable = isRetryable;
    this.originalError = originalError;
    this.timestamp = new Date();

    // Maintains proper stack trace for where error was thrown
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, LambdaError);
    }
  }

  /**
   * Returns a user-friendly error message suitable for display in the UI.
   */
  getUserFriendlyMessage(): string {
    switch (this.type) {
      case LambdaErrorType.TIMEOUT:
        return 'The request timed out. Please try again.';
      case LambdaErrorType.AUTH_FAILURE:
        return 'Your session has expired. Please sign in again.';
      case LambdaErrorType.NETWORK_ERROR:
        return 'Unable to connect. Please check your internet connection and try again.';
      case LambdaErrorType.LAMBDA_ERROR:
        return 'An error occurred while processing your request. Please try again.';
      case LambdaErrorType.PARSE_ERROR:
        return 'Received an unexpected response. Please try again.';
      default:
        return 'An unexpected error occurred. Please try again.';
    }
  }
}

/**
 * Error thrown when a Lambda invocation times out.
 * Requirements: 6.1
 */
export class LambdaTimeoutError extends LambdaError {
  constructor(functionName: string, region: string, originalError?: Error) {
    super(
      `Lambda invocation timed out for ${functionName} in ${region}`,
      LambdaErrorType.TIMEOUT,
      functionName,
      region,
      true, // Timeout errors are retryable
      originalError
    );
    this.name = 'LambdaTimeoutError';
  }
}

/**
 * Error thrown when authentication/authorization fails.
 * Requirements: 6.2
 */
export class LambdaAuthError extends LambdaError {
  constructor(functionName: string, region: string, originalError?: Error) {
    super(
      `Authentication failed for Lambda ${functionName} in ${region}`,
      LambdaErrorType.AUTH_FAILURE,
      functionName,
      region,
      false, // Auth errors require re-authentication, not simple retry
      originalError
    );
    this.name = 'LambdaAuthError';
  }
}

/**
 * Error thrown when a network error occurs.
 * Requirements: 6.3
 */
export class LambdaNetworkError extends LambdaError {
  constructor(functionName: string, region: string, originalError?: Error) {
    super(
      `Network error while invoking Lambda ${functionName} in ${region}`,
      LambdaErrorType.NETWORK_ERROR,
      functionName,
      region,
      true, // Network errors are retryable
      originalError
    );
    this.name = 'LambdaNetworkError';
  }
}

/**
 * Error thrown when the Lambda function returns an error.
 * Requirements: 6.5
 */
export class LambdaFunctionError extends LambdaError {
  public readonly errorType?: string;
  public readonly statusCode?: number;

  constructor(
    functionName: string,
    region: string,
    errorMessage: string,
    errorType?: string,
    statusCode?: number,
    originalError?: Error
  ) {
    super(
      errorMessage || `Lambda function ${functionName} returned an error`,
      LambdaErrorType.LAMBDA_ERROR,
      functionName,
      region,
      false, // Lambda function errors are generally not retryable
      originalError
    );
    this.name = 'LambdaFunctionError';
    this.errorType = errorType;
    this.statusCode = statusCode;
  }
}

/**
 * Error thrown when response parsing fails.
 */
export class LambdaParseError extends LambdaError {
  constructor(functionName: string, region: string, originalError?: Error) {
    super(
      `Failed to parse response from Lambda ${functionName} in ${region}`,
      LambdaErrorType.PARSE_ERROR,
      functionName,
      region,
      false, // Parse errors are not retryable
      originalError
    );
    this.name = 'LambdaParseError';
  }
}

// Response interfaces
/** One record the session wrote to G2S2, as described by the Lambda (deterministic, not model prose). */
export interface WrittenG2S2Record {
  table_name: string;
  /** Exact G2S2 key fields (dimension keys + treatment condition + attribute key). */
  keys: Record<string, string>;
  /** Payload that was written; compared against the prod read to decide "live". */
  data: unknown;
  /** Marketplace used to pick the prod label (empty = default/NA). */
  marketplace?: string;
}

/** What this session has written to G2S2 so far, from the session store. */
export interface G2S2Result {
  label: string;
  stageVersion: string;
  records: WrittenG2S2Record[];
}

export interface StrandsAgentResponse {
  response: string;
  sessionId: string;
  stageVersion?: string;
  previewLabel?: string;
  previewStageVersion?: string;
  g2s2Result?: G2S2Result;
}

/**
 * Result of the StrandsAgents `liveStatus` action: has every record this session
 * wrote reached the prod G2S2 label? Computed server-side under the Lambda's own
 * role so the browser never needs to reach G2S2HelperFunction.
 */
export interface SubmissionLiveStatus {
  live: boolean;
  checked: number;
  matched: number;
  /** Records not yet in prod (keys only). */
  pending: Array<{ table_name: string; keys: Record<string, string> }>;
  /** Set when at least one prod read failed; `live` is false in that case. */
  error?: string;
  /** True when the session has no written records to check (pre-dates tracking, or nothing written). */
  noRecords?: boolean;
  label?: string;
  stageVersion?: string;
}

export interface SlackWebhookResponse {
  success: boolean;
  message: string;
}

export interface G2S2HelperResponse {
  labelName: string;
  stageVersionName: string;
  success: boolean;
}

// Configuration interface for G2S2Helper
export interface G2S2HelperConfig {
  table_name: string;
  g2s2Endpoint?: string;
  abods_config_clientId?: string;
  abods_config_country_code?: string;
  abods_config_deviceFormFactor?: string;
  abods_config_applicationType?: string;
  abods_config_customerSegment?: string;
  abods_config_asinType?: string;
  [key: string]: string | undefined;
}

// AWS Credentials interface
interface AWSCredentials {
  accessKeyId: string;
  secretAccessKey: string;
  sessionToken?: string;
}

/**
 * LambdaService class for direct Lambda invocation with Cognito authentication.
 * 
 * Usage:
 *   const lambdaService = new LambdaService();
 *   const response = await lambdaService.invokeStrandsAgent("Hello", "session-123");
 */
export class LambdaService {
  private lambdaClientWest2: LambdaClient | null = null;
  private lambdaClientEast1: LambdaClient | null = null;
  private credentialsExpiry: Date | null = null;

  // ============================================================================
  // Error Detection and Handling
  // Requirements: 6.1, 6.2, 6.3, 6.4, 6.5
  // ============================================================================

  /**
   * Detects the type of error from an exception and returns the appropriate LambdaError.
   * 
   * @param error - The caught error
   * @param functionName - The Lambda function name
   * @param region - The AWS region
   * @returns A typed LambdaError instance
   */
  private detectErrorType(error: unknown, functionName: string, region: string): LambdaError {
    const err = error as Error & { 
      name?: string; 
      code?: string; 
      $metadata?: { httpStatusCode?: number };
      message?: string;
    };

    // Check for timeout errors
    if (this.isTimeoutError(err)) {
      return new LambdaTimeoutError(functionName, region, err);
    }

    // Check for authentication/authorization errors
    if (this.isAuthError(err)) {
      return new LambdaAuthError(functionName, region, err);
    }

    // Check for network errors
    if (this.isNetworkError(err)) {
      return new LambdaNetworkError(functionName, region, err);
    }

    // Default to generic Lambda error
    return new LambdaError(
      err.message || 'Unknown error occurred',
      LambdaErrorType.UNKNOWN,
      functionName,
      region,
      false,
      err
    );
  }

  /**
   * Checks if the error is a timeout error.
   * Requirements: 6.1
   */
  private isTimeoutError(error: Error & { name?: string; code?: string }): boolean {
    const timeoutIndicators = [
      'timeout',
      'timedout',
      'timed out',
      'ETIMEDOUT',
      'ESOCKETTIMEDOUT',
      'TimeoutError',
      'RequestTimeout',
      'TaskTimedOut',
    ];

    const errorString = `${error.name || ''} ${error.code || ''} ${error.message || ''}`.toLowerCase();
    return timeoutIndicators.some(indicator => errorString.includes(indicator.toLowerCase()));
  }

  /**
   * Checks if the error is an authentication/authorization error.
   * Requirements: 6.2
   */
  private isAuthError(error: Error & { name?: string; code?: string; $metadata?: { httpStatusCode?: number } }): boolean {
    const authIndicators = [
      'UnauthorizedException',
      'AccessDeniedException',
      'NotAuthorizedException',
      'InvalidIdentityToken',
      'ExpiredTokenException',
      'CredentialsError',
      'InvalidSignatureException',
      'SignatureDoesNotMatch',
      'TokenRefreshRequired',
      'AuthError',
    ];

    const errorString = `${error.name || ''} ${error.code || ''} ${error.message || ''}`;
    const hasAuthIndicator = authIndicators.some(indicator => 
      errorString.includes(indicator)
    );

    // Also check HTTP status codes
    const httpStatus = error.$metadata?.httpStatusCode;
    const isAuthStatusCode = httpStatus === 401 || httpStatus === 403;

    return hasAuthIndicator || isAuthStatusCode;
  }

  /**
   * Checks if the error is a network error.
   * Requirements: 6.3
   */
  private isNetworkError(error: Error & { name?: string; code?: string }): boolean {
    const networkIndicators = [
      'NetworkError',
      'NetworkingError',
      'ENOTFOUND',
      'ECONNREFUSED',
      'ECONNRESET',
      'ENETUNREACH',
      'EHOSTUNREACH',
      'EAI_AGAIN',
      'ERR_NETWORK',
      'Failed to fetch',
      'Network request failed',
      'Unable to connect',
      'Connection refused',
      'DNS lookup failed',
    ];

    const errorString = `${error.name || ''} ${error.code || ''} ${error.message || ''}`.toLowerCase();
    return networkIndicators.some(indicator => errorString.toLowerCase().includes(indicator.toLowerCase()));
  }

  /**
   * Parses Lambda function errors from the response.
   * Requirements: 6.5
   * 
   * @param response - The Lambda invoke response
   * @param functionName - The Lambda function name
   * @param region - The AWS region
   * @returns A LambdaFunctionError if there's an error, null otherwise
   */
  private parseLambdaFunctionError(
    response: InvokeCommandOutput,
    functionName: string,
    region: string
  ): LambdaFunctionError | null {
    // Check for FunctionError flag
    if (!response.FunctionError) {
      return null;
    }

    let errorMessage = 'Lambda function returned an error';
    let errorType: string | undefined;
    let statusCode: number | undefined;

    if (response.Payload) {
      try {
        const payloadString = new TextDecoder().decode(response.Payload);
        const errorPayload = JSON.parse(payloadString);
        
        errorMessage = errorPayload.errorMessage || errorPayload.message || errorMessage;
        errorType = errorPayload.errorType || response.FunctionError;
        statusCode = errorPayload.statusCode;
      } catch {
        // If parsing fails, use the FunctionError as the error type
        errorType = response.FunctionError;
      }
    }

    return new LambdaFunctionError(functionName, region, errorMessage, errorType, statusCode);
  }

  /**
   * Logs an error with detailed information for debugging.
   * Requirements: 6.4
   * 
   * @param error - The LambdaError to log
   * @param context - Additional context about the invocation
   */
  private logError(error: LambdaError, context?: Record<string, unknown>): void {
    const logEntry = {
      timestamp: error.timestamp.toISOString(),
      errorType: error.type,
      errorName: error.name,
      message: error.message,
      functionName: error.functionName,
      region: error.region,
      isRetryable: error.isRetryable,
      ...(context && { context }),
      ...(error.originalError && { 
        originalError: {
          name: error.originalError.name,
          message: error.originalError.message,
        }
      }),
    };

    console.error('[LambdaService Error]', JSON.stringify(logEntry, null, 2));
  }

  /**
   * Wraps a Lambda invocation with error handling.
   * Catches errors, detects their type, logs them, and re-throws typed errors.
   * 
   * @param invocationFn - The async function that performs the Lambda invocation
   * @param functionName - The Lambda function name
   * @param region - The AWS region
   * @param context - Additional context for logging
   * @returns The result of the invocation
   */
  private async withErrorHandling<T>(
    invocationFn: () => Promise<T>,
    functionName: string,
    region: string,
    context?: Record<string, unknown>
  ): Promise<T> {
    try {
      return await invocationFn();
    } catch (error) {
      // If it's already a LambdaError, just log and re-throw
      if (error instanceof LambdaError) {
        this.logError(error, context);
        throw error;
      }

      // Detect error type and create appropriate error
      const lambdaError = this.detectErrorType(error, functionName, region);
      this.logError(lambdaError, context);
      throw lambdaError;
    }
  }

  /**
   * Get fresh Cognito credentials from the authenticated session.
   * Forces refresh if credentials are expired or about to expire.
   * 
   * Requirements: 1.1, 1.4
   */
  async getCredentials(): Promise<AWSCredentials> {
    try {
      // Force refresh if credentials are expired or will expire within 5 minutes
      const shouldRefresh = !this.credentialsExpiry || 
        new Date() >= new Date(this.credentialsExpiry.getTime() - 5 * 60 * 1000);

      console.log('[LambdaService] Fetching auth session, forceRefresh:', shouldRefresh);
      const session = await fetchAuthSession({ forceRefresh: shouldRefresh });
      
      if (!session.credentials) {
        console.error('[LambdaService] No credentials in session. User may not be authenticated or Identity Pool may not be configured.');
        console.error('[LambdaService] Session details:', {
          hasTokens: !!session.tokens,
          hasCredentials: !!session.credentials,
          hasIdentityId: !!session.identityId,
        });
        throw new Error('No credentials available. Please ensure you are signed in and the Identity Pool is configured.');
      }

      const { accessKeyId, secretAccessKey, sessionToken, expiration } = session.credentials;

      if (!accessKeyId || !secretAccessKey) {
        console.error('[LambdaService] Invalid credentials - missing required fields');
        throw new Error('Invalid credentials: missing accessKeyId or secretAccessKey');
      }

      // Update expiry tracking
      if (expiration) {
        this.credentialsExpiry = new Date(expiration);
        console.log('[LambdaService] Credentials obtained, expires:', this.credentialsExpiry.toISOString());
      }

      return {
        accessKeyId,
        secretAccessKey,
        sessionToken,
      };
    } catch (error) {
      const err = error as Error;
      console.error('[LambdaService] Failed to get Cognito credentials:', err.message);
      
      // Re-throw with more context - this will be caught by withErrorHandling
      // and classified appropriately
      throw error;
    }
  }

  /**
   * Create a Lambda client for the specified region.
   * Reuses existing clients when credentials are still valid.
   * 
   * Requirements: 1.2, 1.3
   */
  async createLambdaClient(region: string): Promise<LambdaClient> {
    const credentials = await this.getCredentials();

    // Check if we can reuse existing client
    if (region === lambdaConfig.usWest2Region && this.lambdaClientWest2) {
      // Update credentials on existing client
      this.lambdaClientWest2 = new LambdaClient({
        region,
        credentials,
      });
      return this.lambdaClientWest2;
    }

    if (region === lambdaConfig.usEast1Region && this.lambdaClientEast1) {
      // Update credentials on existing client
      this.lambdaClientEast1 = new LambdaClient({
        region,
        credentials,
      });
      return this.lambdaClientEast1;
    }

    // Create new client
    const client = new LambdaClient({
      region,
      credentials,
    });

    // Cache the client
    if (region === lambdaConfig.usWest2Region) {
      this.lambdaClientWest2 = client;
    } else if (region === lambdaConfig.usEast1Region) {
      this.lambdaClientEast1 = client;
    }

    return client;
  }


  /**
   * Invoke the StrandsAgents Lambda with a prompt and session ID.
   * 
   * Payload format: [prompt, sessionId, ownerAlias] array
   * Region: us-west-2
   * 
   * Requirements: 2.1, 2.2, 2.3, 5.2, 5.4, 6.1, 6.2, 6.3, 6.4, 6.5
   * 
   * @param prompt - The user's message/prompt
   * @param sessionId - The session ID (null for new sessions)
   * @param ownerAlias - The user's alias/email for tracking ownership
   * @returns StrandsAgentResponse with response text and session info
   */
  async invokeStrandsAgent(prompt: string, sessionId: string | null, ownerAlias?: string): Promise<StrandsAgentResponse> {
    const functionName = lambdaConfig.strandsAgentsFunctionName;
    const region = lambdaConfig.usWest2Region;

    return this.withErrorHandling(
      async () => {
        const client = await this.createLambdaClient(region);
        
        // Serialize payload as [prompt, sessionId, ownerAlias] array format per design spec
        const payload: [string, string | null, string] = [prompt, sessionId, ownerAlias || ''];
        
        const command = new InvokeCommand({
          FunctionName: functionName,
          Payload: new TextEncoder().encode(JSON.stringify(payload)),
        });

        const response = await client.send(command);
        
        // Check for Lambda function errors
        const functionError = this.parseLambdaFunctionError(response, functionName, region);
        if (functionError) {
          throw functionError;
        }

        return this.parseStrandsAgentResponse(response, functionName, region);
      },
      functionName,
      region,
      { prompt: prompt.substring(0, 100), sessionId }
    );
  }

  /**
   * Parse the raw Lambda response from StrandsAgents.
   * 
   * Expected response formats:
   * 1. Direct format: { response: { role: "assistant", content: [{ text: "..." }] }, sessionId: "..." }
   * 2. Body wrapper: { statusCode: number, body: string (JSON) }
   * 3. Array format: [response, sessionId]
   * 4. Simple object: { response: "string", sessionId: "..." }
   */
  private parseStrandsAgentResponse(
    response: InvokeCommandOutput,
    functionName: string,
    region: string
  ): StrandsAgentResponse {
    if (!response.Payload) {
      throw new LambdaParseError(functionName, region, new Error('Empty response payload'));
    }

    try {
      const payloadString = new TextDecoder().decode(response.Payload);
      console.log('[LambdaService] Raw Lambda response:', payloadString);
      
      const rawResponse = JSON.parse(payloadString);

      // Parse the body if it's wrapped in a body field
      let body = rawResponse;
      if (typeof rawResponse.body === 'string') {
        body = JSON.parse(rawResponse.body);
      } else if (rawResponse.body) {
        body = rawResponse.body;
      }

      // Handle array format [response, sessionId]
      if (Array.isArray(body)) {
        return {
          response: body[0],
          sessionId: body[1],
        };
      }

      // Extract the response text - handle nested Bedrock/Claude format
      // Format: { response: { role: "assistant", content: [{ text: "..." }] }, sessionId: "..." }
      let responseText: string;
      if (typeof body.response === 'object' && body.response !== null) {
        // Nested format from Bedrock - extract text from content array
        const content = body.response.content;
        if (Array.isArray(content) && content.length > 0 && content[0].text) {
          responseText = content[0].text;
        } else {
          // Fallback: stringify the response object
          responseText = JSON.stringify(body.response);
        }
      } else {
        // Simple string format
        responseText = body.response || '';
      }

      return {
        response: responseText,
        sessionId: body.sessionId,
        stageVersion: body.stageVersion,
        previewLabel: body.previewLabel,
        previewStageVersion: body.previewStageVersion,
        g2s2Result: parseG2S2Result(body.g2s2Result),
      };
    } catch (error) {
      throw new LambdaParseError(functionName, region, error as Error);
    }
  }

  /**
   * Invoke the SlackWebhookFunction Lambda.
   * 
   * Payload format: {sessionId, userEmail?, testingUrl?, weblab?, description?} object
   * Region: us-west-2
   * 
   * Requirements: 4.2, 4.3, 4.4, 4.5, 5.2, 5.4, 6.1, 6.2, 6.3, 6.4, 6.5
   * 
   * @param sessionId - The session ID (required, max 128 chars)
   * @param userEmail - Optional user email
   * @param testingUrl - Optional testing URL
   * @param weblab - Optional weblab name with treatment (e.g., "G2S2_1355070:T1")
   * @param description - Optional description of the change
   * @returns SlackWebhookResponse with success status and message
   */
  async invokeSlackWebhook(
    sessionId: string,
    userEmail?: string,
    testingUrl?: string,
    weblab?: string,
    description?: string
  ): Promise<SlackWebhookResponse> {
    const functionName = lambdaConfig.slackWebhookFunctionName;
    const region = lambdaConfig.usWest2Region;

    return this.withErrorHandling(
      async () => {
        const client = await this.createLambdaClient(region);
        
        // Serialize payload as {sessionId, userEmail, testingUrl, weblab, description} object per design spec
        const payload: { 
          sessionId: string; 
          userEmail?: string; 
          testingUrl?: string;
          weblab?: string;
          description?: string;
        } = {
          sessionId,
        };
        
        if (userEmail) {
          payload.userEmail = userEmail;
        }
        if (testingUrl) {
          payload.testingUrl = testingUrl;
        }
        if (weblab) {
          payload.weblab = weblab;
        }
        if (description) {
          payload.description = description;
        }

        const command = new InvokeCommand({
          FunctionName: functionName,
          Payload: new TextEncoder().encode(JSON.stringify(payload)),
        });

        const response = await client.send(command);
        
        // Check for Lambda function errors
        const functionError = this.parseLambdaFunctionError(response, functionName, region);
        if (functionError) {
          throw functionError;
        }

        return this.parseSlackWebhookResponse(response, functionName, region);
      },
      functionName,
      region,
      { sessionId, hasUserEmail: !!userEmail, hasTestingUrl: !!testingUrl, hasWeblab: !!weblab }
    );
  }

  /**
   * Parse the raw Lambda response from SlackWebhookFunction.
   */
  private parseSlackWebhookResponse(
    response: InvokeCommandOutput,
    functionName: string,
    region: string
  ): SlackWebhookResponse {
    if (!response.Payload) {
      throw new LambdaParseError(functionName, region, new Error('Empty response payload'));
    }

    try {
      const payloadString = new TextDecoder().decode(response.Payload);
      
      // Handle plain string responses like "Notification sent" (with or without quotes)
      // First, try to see if it's a simple quoted string
      if (payloadString.startsWith('"') && payloadString.endsWith('"')) {
        // It's a JSON-encoded string, parse it to get the actual string
        const actualString = JSON.parse(payloadString);
        const isSuccess = actualString.toLowerCase().includes('sent') || 
                          actualString.toLowerCase().includes('success') ||
                          actualString.toLowerCase().includes('notification');
        return {
          success: isSuccess,
          message: actualString,
        };
      }
      
      // Handle unquoted plain string responses
      if (!payloadString.startsWith('{') && !payloadString.startsWith('[')) {
        // Plain string response - treat as success if it contains positive indicators
        const isSuccess = payloadString.toLowerCase().includes('sent') || 
                          payloadString.toLowerCase().includes('success') ||
                          payloadString.toLowerCase().includes('notification');
        return {
          success: isSuccess,
          message: payloadString,
        };
      }
      
      const rawResponse = JSON.parse(payloadString);

      // Handle body - it could be a JSON string, a plain string, or an object
      let message = 'Operation completed';
      let success = rawResponse.statusCode === 200;
      
      if (rawResponse.body) {
        if (typeof rawResponse.body === 'string') {
          // Check if body is JSON or plain string
          if (rawResponse.body.startsWith('{') || rawResponse.body.startsWith('[')) {
            // Body is a JSON string, parse it
            const bodyObj = JSON.parse(rawResponse.body);
            success = bodyObj.success ?? success;
            message = bodyObj.message || rawResponse.body;
          } else {
            // Body is a plain string like "Notification sent"
            message = rawResponse.body;
            // Infer success from the message content
            const lowerBody = rawResponse.body.toLowerCase();
            success = success || lowerBody.includes('sent') || 
                      lowerBody.includes('success') || 
                      lowerBody.includes('notification');
          }
        } else if (typeof rawResponse.body === 'object') {
          // Body is already an object
          success = rawResponse.body.success ?? success;
          message = rawResponse.body.message || message;
        }
      }

      return { success, message };
    } catch (error) {
      throw new LambdaParseError(functionName, region, error as Error);
    }
  }

  /**
   * Invoke the G2S2HelperFunction Lambda.
   * 
   * Payload format: JSON object with table_name and config fields
   * Region: us-east-1
   * 
   * Requirements: 5.2, 5.4, 6.1, 6.2, 6.3, 6.4, 6.5
   * 
   * @param config - G2S2HelperConfig object with table_name and optional config fields
   * @returns G2S2HelperResponse with label and stage version info
   */
  async invokeG2S2Helper(config: G2S2HelperConfig): Promise<G2S2HelperResponse> {
    const functionName = lambdaConfig.g2s2HelperFunctionName;
    const region = lambdaConfig.usEast1Region;

    return this.withErrorHandling(
      async () => {
        const client = await this.createLambdaClient(region);
        
        // Serialize payload as JSON object per design spec
        const command = new InvokeCommand({
          FunctionName: functionName,
          Payload: new TextEncoder().encode(JSON.stringify(config)),
        });

        const response = await client.send(command);
        
        // Check for Lambda function errors
        const functionError = this.parseLambdaFunctionError(response, functionName, region);
        if (functionError) {
          throw functionError;
        }

        return this.parseG2S2HelperResponse(response, functionName, region);
      },
      functionName,
      region,
      { tableName: config.table_name }
    );
  }

  /**
   * Parse the raw Lambda response from G2S2HelperFunction.
   */
  private parseG2S2HelperResponse(
    response: InvokeCommandOutput,
    functionName: string,
    region: string
  ): G2S2HelperResponse {
    if (!response.Payload) {
      throw new LambdaParseError(functionName, region, new Error('Empty response payload'));
    }

    try {
      const payloadString = new TextDecoder().decode(response.Payload);
      const rawResponse = JSON.parse(payloadString);

      // Parse body if it's a string
      let body: { labelName: string; stageVersionName: string; success?: boolean };
      
      if (typeof rawResponse.body === 'string') {
        body = JSON.parse(rawResponse.body);
      } else {
        body = rawResponse.body || rawResponse;
      }

      return {
        labelName: body.labelName || '',
        stageVersionName: body.stageVersionName || '',
        success: body.success ?? (rawResponse.statusCode === 200),
      };
    } catch (error) {
      throw new LambdaParseError(functionName, region, error as Error);
    }
  }

  /**
   * Ask StrandsAgents whether a submitted change is live. Structured (non-chat)
   * event: {"action":"liveStatus","sessionId"}. Same function and permissions
   * the chat already uses; nothing new for the browser role to reach.
   * Region: us-west-2
   */
  async getSubmissionLiveStatus(sessionId: string): Promise<SubmissionLiveStatus> {
    const functionName = lambdaConfig.strandsAgentsFunctionName;
    const region = lambdaConfig.usWest2Region;

    return this.withErrorHandling(
      async () => {
        const client = await this.createLambdaClient(region);
        const command = new InvokeCommand({
          FunctionName: functionName,
          Payload: new TextEncoder().encode(JSON.stringify({ action: 'liveStatus', sessionId })),
        });
        const response = await client.send(command);
        const functionError = this.parseLambdaFunctionError(response, functionName, region);
        if (functionError) {
          throw functionError;
        }
        if (!response.Payload) {
          throw new LambdaParseError(functionName, region, new Error('Empty response payload'));
        }
        let statusCode: number | undefined;
        let body: unknown;
        try {
          const raw = JSON.parse(new TextDecoder().decode(response.Payload));
          statusCode = typeof raw.statusCode === 'number' ? raw.statusCode : undefined;
          body = typeof raw.body === 'string' ? JSON.parse(raw.body) : (raw.body || raw);
        } catch (error) {
          throw new LambdaParseError(functionName, region, error as Error);
        }
        if (statusCode !== undefined && statusCode >= 400) {
          // The Lambda answered but rejected the request (e.g. bad sessionId); keep its message.
          const message = (body as { error?: unknown } | null)?.error;
          throw new LambdaFunctionError(
            functionName,
            region,
            typeof message === 'string' && message ? message : `liveStatus returned ${statusCode}`
          );
        }
        return parseLiveStatus(body);
      },
      functionName,
      region,
      { sessionId }
    );
  }
}

/** Accept only a well-formed g2s2Result; anything else is treated as absent. */
function parseG2S2Result(value: unknown): G2S2Result | undefined {
  if (!value || typeof value !== 'object') return undefined;
  const v = value as Record<string, unknown>;
  if (typeof v.label !== 'string' || !v.label) return undefined;
  const records = Array.isArray(v.records)
    ? (v.records as unknown[]).filter(
        (r): r is WrittenG2S2Record =>
          !!r && typeof r === 'object'
          && typeof (r as WrittenG2S2Record).table_name === 'string'
          && !!(r as WrittenG2S2Record).keys && typeof (r as WrittenG2S2Record).keys === 'object'
      )
    : [];
  return { label: v.label, stageVersion: typeof v.stageVersion === 'string' ? v.stageVersion : '', records };
}

/** Coerce the liveStatus body into a SubmissionLiveStatus; unknown shapes become a reported error, never "live". */
function parseLiveStatus(body: unknown): SubmissionLiveStatus {
  const b = (body && typeof body === 'object' ? body : {}) as Record<string, unknown>;
  const asNum = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) ? v : 0);
  const pending = Array.isArray(b.pending)
    ? (b.pending as unknown[]).filter((r): r is { table_name: string; keys: Record<string, string> } =>
        !!r && typeof r === 'object' && typeof (r as { table_name?: unknown }).table_name === 'string')
    : [];
  const status: SubmissionLiveStatus = {
    live: b.live === true,
    checked: asNum(b.checked),
    matched: asNum(b.matched),
    pending,
  };
  if (typeof b.error === 'string' && b.error) status.error = b.error;
  if (b.noRecords === true) status.noRecords = true;
  if (typeof b.label === 'string') status.label = b.label;
  if (typeof b.stageVersion === 'string') status.stageVersion = b.stageVersion;
  if (typeof b.live !== 'boolean' && !status.error) status.error = 'Unexpected liveStatus response';
  return status;
}

// Export a singleton instance for convenience
export const lambdaService = new LambdaService();

export default LambdaService;
